import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../../api/client'
import type { ItemKind } from './itemApi'

export interface Attachment {
  id: string
  fileName: string
  contentType: string
  sizeBytes: number
  uploadedByUserId: string
  uploadedByDisplayName: string
  uploadedAt: string
  downloadUrl: string
}

function attachmentsRoot(eventId: string, kind: ItemKind, itemId: string): string {
  const segment = kind === 'Milestone' ? 'milestones' : 'items'
  return `/api/events/${eventId}/plan/${segment}/${itemId}/attachments`
}

export function useAttachments(eventId: string, kind: ItemKind, itemId: string) {
  return useQuery({
    queryKey: ['attachments', itemId],
    queryFn: () => apiFetch<Attachment[]>(attachmentsRoot(eventId, kind, itemId)),
  })
}

export function useUploadAttachment(eventId: string, kind: ItemKind, itemId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file, file.name)

      // rawBody, not JSON: the browser must set the multipart boundary itself.
      return apiFetch<Attachment>(attachmentsRoot(eventId, kind, itemId), {
        method: 'POST',
        rawBody: form,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['attachments', itemId] })
      void queryClient.invalidateQueries({ queryKey: ['plan', eventId] })
    },
  })
}

export function useDeleteAttachment(eventId: string, kind: ItemKind, itemId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (attachmentId: string) =>
      apiFetch<void>(`${attachmentsRoot(eventId, kind, itemId)}/${attachmentId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['attachments', itemId] })
      void queryClient.invalidateQueries({ queryKey: ['plan', eventId] })
    },
  })
}

export const MAX_IMAGE_EDGE = 2048

/**
 * Raster formats that survive a decode-and-redraw with nothing lost but pixels.
 *
 * Deliberately a list rather than an `image/` prefix test. SVG is an image by
 * content type and a document by nature: rasterizing one throws away everything
 * that made it worth attaching, and it would happen silently, on a file the
 * person chose precisely because it was vector.
 */
const DOWNSCALABLE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/bmp',
  'image/gif',
])

/**
 * Shrinks a large photo before upload.
 *
 * A phone camera produces images far larger than anyone needs to read a
 * shipping label, and venue Wi-Fi is usually poor. Anything that is not a
 * downscalable raster image - a PDF, a spreadsheet, an SVG, or an image the
 * browser cannot decode - is returned untouched rather than rejected here; the
 * server decides what is allowed.
 */
export async function downscaleImage(file: File, maxEdge = MAX_IMAGE_EDGE): Promise<File> {
  if (!DOWNSCALABLE_TYPES.has(file.type.toLowerCase()) || typeof createImageBitmap !== 'function') {
    return file
  }

  try {
    const bitmap = await createImageBitmap(file)
    const longestEdge = Math.max(bitmap.width, bitmap.height)

    if (longestEdge <= maxEdge) {
      bitmap.close()
      return file
    }

    const scale = maxEdge / longestEdge
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)

    const context = canvas.getContext('2d')
    if (!context) {
      bitmap.close()
      return file
    }

    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    )

    if (!blob) {
      return file
    }

    // Keep a .jpg name so the server's extension check agrees with the bytes
    // it is now being sent.
    const baseName = file.name.replace(/\.[^./\\]+$/, '')
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' })
  } catch {
    // If anything about decoding fails, send the original and let the server judge.
    return file
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
